// Generic reusable date-picker wiring for charts with preset buttons and a range input.
// Usage:
// wireDatePicker({
//   rootEl, // container element (defaults to document)
//   onChange, // function(payload)
//   inputSelector: '#range-input',
//   buttonSelector: '[data-range]...',
//   hiddenFromSelector: '#from', // optional
//   hiddenToSelector: '#to',     // optional
//   maxDate: 'today', // flatpickr maxDate
//   activeClasses: { add: ['bg-active','text-white'], remove: ['bg-white','text-gray-700'] }
// })

// One-time global redirect so "Default" forwards to the first non-default button in its group
(function installGlobalDefaultRedirect() {
  if (window.__quillDefaultRedirectInstalled) return;
  window.__quillDefaultRedirectInstalled = true;

  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest && e.target.closest('button');
    if (!btn) return;

    const label = (btn.textContent || '').trim().toLowerCase();
    if (label !== 'default') return;

    // Only act if in a group with range buttons
    const group = btn.closest('span') || btn.parentElement;
    if (!group) return;

    const candidates = Array.from(group.querySelectorAll('button'));
    const first = candidates.find(b =>
      b !== btn &&
      (b.matches('[data-range]') || b.matches('[data-ep-range]')) &&
      (b.textContent || '').trim().toLowerCase() !== 'default'
    );
    if (!first) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    first.click();
  }, true);
})();

export function wireDatePicker(opts = {}) {
  const root = opts.rootEl || document;
  const onChange = typeof opts.onChange === 'function' ? opts.onChange : () => {};
  const inputSelector = opts.inputSelector || '#date-range';
  const buttonSelector = opts.buttonSelector || '[data-range], [data-ep-range]';
  const hiddenFromSelector = opts.hiddenFromSelector || null;
  const hiddenToSelector = opts.hiddenToSelector || null;
  const maxDate = opts.maxDate || 'today';
  const classes = opts.activeClasses || { add: ['bg-quill-active','text-white'], remove: ['bg-white','text-gray-700'] };

  let picker = null;
  let rangeClickHandler = null;

  function fmt(d) { return d.toISOString().slice(0, 10); }

  function setActive(range, clickedBtn) {
    try {
      const buttons = root.querySelectorAll(buttonSelector);
      buttons.forEach(btn => {
        classes.add.forEach(c => btn.classList.remove(c));
        classes.remove.forEach(c => btn.classList.add(c));
      });
      if (clickedBtn) {
        classes.remove.forEach(c => clickedBtn.classList.remove(c));
        classes.add.forEach(c => clickedBtn.classList.add(c));
        return;
      }
      const sel = `${buttonSelector}[data-range="${range}"], ${buttonSelector}[data-ep-range="${range}"]`;
      const b = root.querySelector(sel);
      if (b) {
        classes.remove.forEach(c => b.classList.remove(c));
        classes.add.forEach(c => b.classList.add(c));
      }
    } catch (_) {/* ignore */}
  }

  function writeHidden(from, to) {
    try {
      if (hiddenFromSelector) {
        const fromEl = document.querySelector(hiddenFromSelector);
        if (fromEl) fromEl.value = from;
      }
      if (hiddenToSelector) {
        const toEl = document.querySelector(hiddenToSelector);
        if (toEl) toEl.value = to;
      }
    } catch (_) {/* ignore */}
  }

  // Preset buttons
  rangeClickHandler = (e) => {
    const clickedBtn = e.target.closest(buttonSelector);
    if (!clickedBtn || !root.contains(clickedBtn)) return;
    e.preventDefault && e.preventDefault();

    const isDefaultBtn = (btn) => {
      const label = (btn.textContent || '').trim().toLowerCase();
      return label === 'default' || (btn.id && btn.id.startsWith('default-'));
    };
    const resolveFirstNonDefault = (btn) => {
      const group = btn.closest('span') || btn.parentElement;
      if (!group) return btn;
      const candidates = Array.from(group.querySelectorAll(buttonSelector));
      const first = candidates.find(b => !isDefaultBtn(b));
      return first || btn;
    };

    const btn = isDefaultBtn(clickedBtn) ? resolveFirstNonDefault(clickedBtn) : clickedBtn;

    // Support both data-range and data-ep-range
    const raw = btn.dataset.range || btn.dataset.epRange || '';

    if (raw === 'custom' || raw === 'custom-range') {
      try { if (picker && typeof picker.open === 'function') picker.open(); } catch (_) {}
      setActive('custom', btn);
      return;
    }
    if (raw === 'all_time' || raw === 'all-time' || raw === 'alltime') {
      setActive('all_time', btn);
      onChange({ type: 'all_time' });
      return;
    }
    const days = Number((raw || '').replace(/\D/g, '')) || null;
    if (days) {
      setActive(days, btn);
      onChange({ type: 'preset', days });
    }
  };
  root.addEventListener('click', rangeClickHandler);

  // Flatpickr instance
  try {
    const inputEl = root.querySelector(inputSelector);
    const FP = (typeof flatpickr !== 'undefined') ? flatpickr : (typeof globalThis !== 'undefined' ? globalThis.flatpickr : undefined);
    if (inputEl && typeof FP === 'function') {
      picker = FP(inputEl, { mode: 'range', maxDate });
      picker.config.onChange.push(function(selectedDates) {
        if (selectedDates.length === 2) {
          const from = fmt(selectedDates[0]);
          const to = fmt(selectedDates[1]);
          writeHidden(from, to);
          setActive('custom');
          onChange({ type: 'custom', from, to });
        }
      });
    }
  } catch (_) { /* ignore */ }

  // Auto-highlight default button (e.g., All Time) on first load
  try {
    const defBtn = root.querySelector(`${buttonSelector}[data-default="true"]`);
    if (defBtn) setActive(null, defBtn);
  } catch (_) { /* ignore */ }

  return {
    openPicker() { try { if (picker && typeof picker.open === 'function') picker.open(); } catch (_) {} },
    setActive,
    setPreset(days) { setActive(days); onChange({ type: 'preset', days }); },
    setCustom(from, to) { writeHidden(from, to); setActive('custom'); onChange({ type: 'custom', from, to }); },
    destroy() {
      try { if (picker && typeof picker.destroy === 'function') picker.destroy(); } catch (_) {}
      if (rangeClickHandler) {
        try { root.removeEventListener('click', rangeClickHandler); } catch (_) {}
        rangeClickHandler = null;
      }
      picker = null;
    }
  };
}
